








import { PropType, computed, defineComponent, onMounted, ref, useContext, useFetch, useRoute } from '@nuxtjs/composition-api';
import { SfLoader } from '@storefront-ui/vue';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import { merge } from 'lodash-es';
import AnalysisPackagesSection from '~/components/sections/AnalysisPackagesSection.vue';
import LoadWhenVisible from '~/components/utils/LoadWhenVisible.vue';
import { useApi, useWishlist } from '~/composables';
import { getAbsolutePageUrl } from '~/helpers/buildSeoHead';
import { getProductMetaInfo } from '~/helpers/getProductMetaInfo';
import type { ProductDetailsQuery } from '~/modules/GraphQL/types';
import AnalysisSkeleton from '~/modules/catalog/product/components/loading/AnalysisSkeleton.vue';
import VirtualProduct from '~/modules/catalog/product/components/product-types/virtual/VirtualProduct.vue';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import getAnalysisDetailsGql from '~/modules/catalog/product/queries/getAnalysisDetails.gql';
import getProductPriceBySkuGql from '~/modules/catalog/product/queries/getProductPriceBySku.gql';
import { usePageStore } from '~/stores/page';
import { CustomProduct } from '../types';

export default defineComponent({
  name: 'ProductPage',
  components: {
    LoadWhenVisible,
    VirtualProduct,
    SfLoader,
    AnalysisPackagesSection,
    AnalysisSkeleton,
  },
  transition: 'fade',
  props: {
    defaultProduct: {
      type: Object as PropType<CustomProduct>,
      required: false,
    },
  },
  setup(props) {
    const pageStore = usePageStore();
    const { query } = useApi();
    const product = ref<CustomProduct | null>(null);
    const loading = ref(true);
    const { addTags } = useCache();
    const route = useRoute();
    const { error: nuxtError } = useContext();
    const { load: loadWishlist } = useWishlist();
    const analysisSlug = computed(() => route.value.params?.slug as string | undefined);

    const getSearchConfigurations = () =>
      Object.entries(route.value.query)
        .filter((config) => config[0] !== 'wishlist')
        .map((config) => config[1]) as string[];

    const getSearchQueryByUrlKey = (slug: string) => ({
      filter: {
        url_key: {
          eq: slug,
        },
      },
      configurations: getSearchConfigurations(),
    });

    const getSearchQueryBySku = (sku?: string) => ({
      filter: {
        sku: {
          eq: sku,
        },
      },
      configurations: getSearchConfigurations(),
    });

    const getBaseSearchQuery = () => {
      if (analysisSlug.value) {
        return getSearchQueryByUrlKey(analysisSlug.value);
      }

      return getSearchQueryBySku(props.defaultProduct?.sku);
    };

    const matchesRequestedAnalysis = (item?: CustomProduct | null) => {
      if (!item) return false;
      if (!analysisSlug.value) return true;
      if (!item.url_key) return true;

      return item.url_key === analysisSlug.value;
    };

    // eslint-disable-next-line no-underscore-dangle
    const renderer = computed(() => product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT);

    const fetchProductBaseData = async (searchQuery = getBaseSearchQuery()) => {
      loading.value = true;
      const { data } = await query<ProductDetailsQuery>(getAnalysisDetailsGql, searchQuery);
      let nextProduct = data.products.items?.[0] as CustomProduct | undefined;

      if (!matchesRequestedAnalysis(nextProduct) && pageStore.routeData?.sku) {
        const fallback = await query<ProductDetailsQuery>(getAnalysisDetailsGql, getSearchQueryBySku(pageStore.routeData.sku));
        const fallbackProduct = fallback.data.products.items?.[0] as CustomProduct | undefined;
        if (matchesRequestedAnalysis(fallbackProduct)) {
          nextProduct = fallbackProduct;
        } else {
          nextProduct = undefined;
        }
      }

      product.value = nextProduct ?? null;
    };

    const fetchProductExtendedData = async () => {
      const sku = product.value?.sku;
      if (!sku) return;

      const { data } = await query<ProductDetailsQuery>(getProductPriceBySkuGql, getSearchQueryBySku(sku), 'no-cache');

      product.value = merge({}, product.value, data.products?.items?.[0] as CustomProduct);
    };

    useFetch(async () => {
      if (analysisSlug.value) {
        await fetchProductBaseData();
      } else if (props.defaultProduct) {
        product.value = props.defaultProduct;
      }
      if (Boolean(product?.value?.sku) === false) {
        loading.value = false;
        nuxtError({ statusCode: 404 });
        return;
      }

      await fetchProductExtendedData();
      loading.value = false;

      const cacheSku = product.value.sku;
      const tags = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${cacheSku}`,
        },
      ];

      if (product.value.uid) {
        tags.push({
          prefix: CacheTagPrefix.Product,
          value: product.value.uid,
        });
      }

      addTags(tags);
    });

    onMounted(async () => {
      // Dirty fix. Scroll to top and compute value to prevent bug
      window.scrollTo(0, 0);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      window.scrollY;
      await loadWishlist();
    });

    return {
      renderer,
      loading,
      product,
    };
  },
  head() {
    const isProductDetailRoute = Boolean(this.$route.params.slug);
    if (!isProductDetailRoute) {
      return {};
    }

    const i18n = this.$i18n as { __baseUrl?: string };

    return getProductMetaInfo(this.product, {
      // eslint-disable-next-line no-underscore-dangle
      url: getAbsolutePageUrl(this.$route.path, i18n.__baseUrl),
      includeJsonLd: true,
    });
  },
});
